package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable
import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.DataField
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.ast.dataClassDecls
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.TypeResolver

/**
 * M6-a：data 类型 + Map 字面量的编译期一致性校验。
 *
 * 当赋值目标类型是已声明 data 时：
 *  - Map 字面量必填字段缺失 → E749
 *  - Map 字面量字段值类型与 data 字段类型不匹配 → E751
 *  - Map 字面量含 data 未声明字段 → W730（不致命，仅提示拼写）
 *  - 嵌套 data：从字段类型反向推断，递归校验
 *
 * 同时维护 `var x: DataName = ...` 形态的局部类型表，用于：
 *  - `x.field` 引用 / `x.field = rhs` 赋值时校验字段名是否存在（E756）
 *
 * 未声明的 data 类型名（例如 `var x: Item = ...` 但没 `data "Item"`）报 E748。
 * 注意：此处 E748 在 [TypeConstraintChecker] 的 E621 之外独立触发——若 data 名同时也
 * 不在内置类型表里，两个错误都会出现，保留 E748 提示更精确的修复路径。
 *
 * M10 Stage 8 bugfix：跨文件场景下，data 类可能在另一个 .allin 文件中定义并通过
 * [crossFileTypes] 注入；判断未声明 data 类型时必须同时查询 [crossFileTypes.dataFields]，
 * 否则 IDE 打开引用跨文件 data 类的文件会误报 E748。
 */
class DataLiteralChecker(
    private val diag: DiagnosticCollector,
    private val crossFileTypes: ProjectTypeTable = ProjectTypeTable.EMPTY,
) {

    /** screen 内声明的 data：name → decl。重复 data 名由本 checker 在 check 阶段以 E765 上报。 */
    private val dataTypes: MutableMap<String, ClassDecl> = linkedMapOf()

    fun check(screen: ScreenAst) {
        // E765：data 同名重复声明 —— 保留首个，逐个上报后续重名（不阻止编译继续）
        for (d in screen.dataClassDecls()) {
            if (dataTypes.containsKey(d.name)) {
                diag.error(DslError.E765_DUPLICATE_DATA_DECL, d.position, d.name)
            } else {
                dataTypes[d.name] = d
            }
        }
        screen.funs.forEach { walkBody(it.body, LocalDataTypes()) }
        // 元素属性表达式中允许出现 data 字面量（罕见），保持递归扫描
        walkElement(screen.root, LocalDataTypes())
    }

    private fun walkElement(
        element: top.wcpe.allincore.dsl.compiler.ast.ElementAst,
        locals: LocalDataTypes,
    ) {
        element.props.forEach { walkExpr(it.value, locals) }
        element.modifiers.forEach { modifier ->
            modifier.fields.forEach { walkExpr(it.value, locals) }
        }
        // RFC-RECOMPOSITION：conditional 的 subject / branch.condition 也可能含 data 字面量 / 字段访问
        element.conditionalContent?.let { content ->
            content.subject?.let { walkExpr(it, locals) }
            for (branch in content.branches) {
                branch.condition?.let { walkExpr(it, locals) }
            }
        }
        // $for：iterable 表达式也可能含 data 字面量 / 字段访问
        element.forContent?.let { walkExpr(it.iterable, locals) }
        top.wcpe.allincore.dsl.compiler.ast.ElementWalk.directDescendants(element).forEach {
            walkElement(it, locals)
        }
    }

    private fun walkBody(stmts: List<StmtAst>, locals: LocalDataTypes) {
        stmts.forEach { walkStmt(it, locals) }
    }

    private fun walkStmt(s: StmtAst, locals: LocalDataTypes) {
        when (s) {
            is StmtAst.VarDeclStmt -> {
                walkExpr(s.initializer, locals)
                val annotation = s.typeAnnotation
                if (annotation != null) {
                    handleVarAnnotation(s.name, annotation, s.initializer, locals)
                }
            }
            is StmtAst.AssignStmt -> {
                walkExpr(s.rhs, locals)
                // x.field = rhs：若 x 是已知 data 类型，校验字段名存在
                val lhs = s.lhs
                if (lhs is LValueAst.IndexLValue) {
                    walkExpr(lhs.target, locals)
                    walkExpr(lhs.index, locals)
                }
            }
            is StmtAst.ExprStmt -> walkExpr(s.call, locals)
            is StmtAst.IfStmt -> {
                walkExpr(s.cond, locals)
                walkBody(s.thenBody, locals)
                walkBody(s.elseBody, locals)
            }
            is StmtAst.WhileStmt -> {
                walkExpr(s.cond, locals); walkBody(s.body, locals)
            }
            is StmtAst.ForInStmt -> {
                walkExpr(s.iterable, locals); walkBody(s.body, locals)
            }
            is StmtAst.WhenStmt -> walkExpr(s.whenExpr, locals)
            is StmtAst.ReturnStmt -> s.value?.let { walkExpr(it, locals) }
            is StmtAst.TryStmt -> {
                walkBody(s.tryBody, locals); walkBody(s.catchBody, locals); walkBody(s.finallyBody, locals)
            }
            // batch 语句：递归遍历 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> walkBody(s.body, locals)
            is StmtAst.FunDeclStmt -> walkBody(s.decl.body, LocalDataTypes())
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    /**
     * `var name: T = init`：
     *  - 若 T 指向已声明 data，记录 `name -> T` 局部类型；并按 T 校验 init 字面量
     *  - 若 T 是 List<DataName> / Map<String, DataName>，仅按元素类型递归校验字面量
     *  - 若 T 是 Named 但既不是内置也不是 data，报 E748
     */
    private fun handleVarAnnotation(
        name: String,
        annotation: TypeAst,
        initializer: ExprAst,
        locals: LocalDataTypes,
    ) {
        val dataName = extractDataTypeName(annotation)
        if (dataName != null) {
            locals.put(name, dataName)
            checkExprAgainstType(initializer, annotation)
            return
        }
        // M6-a：Named 类型既非内置（TypeResolver 解析失败）也非已声明 data → 视为未声明的 data 类型。
        // M10 Stage 8：跨文件 data 类（位于其它 .allin 文件中、由 IDE 通过 crossFileTypes 注入）
        // 也应视为已声明，否则会在打开引用文件时误报 E748。
        if (annotation is TypeAst.Named
            && TypeResolver.resolve(annotation) == null
            && annotation.name !in dataTypes
            && annotation.name !in crossFileTypes.dataFields.keys
        ) {
            diag.error(
                DslError.E748_UNDECLARED_DATA_TYPE,
                annotation.position,
                "未声明的 data 类型 '${annotation.name}'",
            )
            return
        }
        // List<DataName> / Map<String, DataName>：仅元素层递归
        checkExprAgainstType(initializer, annotation)
    }

    /** 从 TypeAst 中提取顶层 data 类型名；不是 data 引用时返回 null。 */
    private fun extractDataTypeName(t: TypeAst): String? = when (t) {
        is TypeAst.Named -> if (t.name in dataTypes) t.name else null
        else -> null
    }

    /**
     * 把表达式按目标类型 [type] 做 data 一致性校验：
     *  - type == data：要求表达式为 Map 字面量并按 data 字段做必填/类型/未知字段检查
     *  - type == List<X>：表达式为列表字面量时，元素按 X 递归
     *  - type == Map<String, X>：表达式为 Map 字面量时，每个 value 按 X 递归（不强制字段）
     *  - 其它：放行
     */
    private fun checkExprAgainstType(expr: ExprAst, type: TypeAst) {
        // data 类型
        val dataName = extractDataTypeName(type)
        if (dataName != null) {
            val decl = dataTypes[dataName] ?: return
            if (expr is ExprAst.MapLiteralExpr) {
                checkMapAgainstData(expr, decl)
            }
            return
        }
        // 泛型容器
        if (type is TypeAst.Generic) {
            when (type.name) {
                "List" -> {
                    if (expr is ExprAst.ListLiteralExpr && type.args.isNotEmpty()) {
                        val elemType = type.args[0]
                        expr.elements.forEach { checkExprAgainstType(it, elemType) }
                    }
                }
                "Map" -> {
                    if (expr is ExprAst.MapLiteralExpr && type.args.size >= 2) {
                        val valueType = type.args[1]
                        expr.entries.forEach { checkExprAgainstType(it.value, valueType) }
                    }
                }
            }
        }
    }

    /** 校验 [map] 是否符合 [decl] 的字段约束。 */
    private fun checkMapAgainstData(map: ExprAst.MapLiteralExpr, decl: ClassDecl) {
        val fieldByName = decl.fields.associateBy { it.name }
        val provided = linkedSetOf<String>()
        for (entry in map.entries) {
            provided += entry.key
            val field = fieldByName[entry.key]
            if (field == null) {
                // W730：多余字段（疑似拼写错误），不致命
                diag.warning(
                    DslError.W730_MAP_LITERAL_UNKNOWN_DATA_FIELD,
                    entry.position,
                    decl.name, entry.key,
                )
                continue
            }
            // 类型不匹配检查（E751）：第一版仅对静态可判定的类型做严格检查
            val mismatch = detectFieldTypeMismatch(field.type, entry.value)
            if (mismatch != null) {
                diag.error(
                    DslError.E751_DATA_FIELD_TYPE_MISMATCH,
                    entry.value.position,
                    decl.name, field.name, typeDisplay(field.type), mismatch,
                )
            }
            // 嵌套 data：递归
            checkExprAgainstType(entry.value, field.type)
        }
        // E749：必填字段缺失
        for (f in decl.fields) {
            if (f.default == null && f.name !in provided) {
                diag.error(
                    DslError.E749_DATA_REQUIRED_FIELD_MISSING,
                    map.position,
                    decl.name, f.name,
                )
            }
        }
    }

    /**
     * 静态字段类型不匹配检测：仅对"字面量值 vs 已知基础类型"做严格判定。
     * 返回不为 null 时表示检测到不匹配，值为实际类型展示名；返回 null 表示通过 / 无法判定。
     */
    private fun detectFieldTypeMismatch(expected: TypeAst, value: ExprAst): String? {
        if (expected !is TypeAst.Named) return null
        return when (expected.name) {
            "String" -> {
                if (value is ExprAst.Literal.NumberLit) "Number"
                else if (value is ExprAst.Literal.BoolLit) "Boolean"
                else null
            }
            "Int", "Long", "Double", "Float", "Number" -> {
                if (value is ExprAst.Literal.StringLit) "String"
                else if (value is ExprAst.Literal.BoolLit) "Boolean"
                else if (value is ExprAst.Interp) "String"
                else null
            }
            "Bool", "Boolean" -> {
                if (value is ExprAst.Literal.NumberLit) "Number"
                else if (value is ExprAst.Literal.StringLit) "String"
                else null
            }
            else -> null
        }
    }

    /** TypeAst 文本展示。 */
    private fun typeDisplay(t: TypeAst): String = when (t) {
        is TypeAst.Named -> if (t.nullable) "${t.name}?" else t.name
        is TypeAst.Generic -> {
            val args = t.args.joinToString(", ") { typeDisplay(it) }
            if (t.nullable) "${t.name}<$args>?" else "${t.name}<$args>"
        }
        is TypeAst.Function -> "(..)->.."
    }

    /** 表达式遍历：识别 data 局部变量的字段访问 / 赋值，校验字段名（E756）。 */
    private fun walkExpr(e: ExprAst, locals: LocalDataTypes) {
        when (e) {
            is ExprAst.MemberAccess -> {
                checkDataMember(e.target, e.member, e.position, locals)
                walkExpr(e.target, locals)
            }
            is ExprAst.SafeMemberAccess -> {
                checkDataMember(e.target, e.member, e.position, locals)
                walkExpr(e.target, locals)
            }
            // M10 Stage 6：`target!!` 透传 target（无字段访问语义）
            is ExprAst.NonNullAssert -> walkExpr(e.target, locals)
            is ExprAst.LambdaExpr -> walkBody(e.body, LocalDataTypes())
            is ExprAst.IfExpr -> {
                walkExpr(e.cond, locals); walkBody(e.thenBody, locals); walkBody(e.elseBody, locals)
            }
            is ExprAst.WhenExpr -> {
                walkExpr(e.subject, locals)
                e.branches.forEach { br ->
                    br.values.forEach { walkExpr(it, locals) }
                    walkBody(br.body, locals)
                }
                e.elseBody?.let { walkBody(it, locals) }
            }
            is ExprAst.CallExpr -> {
                walkExpr(e.callee, locals)
                e.args.forEach { walkExpr(it, locals) }
                e.namedArgs.forEach { walkExpr(it.value, locals) }
            }
            is ExprAst.AwaitExpr -> {
                e.netRequestCall?.let { walkExpr(it, locals) }
                e.suspendCall?.let { walkExpr(it, locals) }
                e.tailExpr?.let { walkExpr(it, locals) }
            }
            is ExprAst.BinaryOp -> { walkExpr(e.left, locals); walkExpr(e.right, locals) }
            is ExprAst.UnaryOp -> walkExpr(e.operand, locals)
            is ExprAst.MemberCall -> {
                walkExpr(e.target, locals); e.args.forEach { walkExpr(it, locals) }
                e.namedArgs.forEach { walkExpr(it.value, locals) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExpr(e.target, locals); e.args.forEach { walkExpr(it, locals) }
            }
            is ExprAst.Elvis -> { walkExpr(e.left, locals); walkExpr(e.right, locals) }
            is ExprAst.Ternary -> {
                walkExpr(e.cond, locals); walkExpr(e.thenExpr, locals); walkExpr(e.elseExpr, locals)
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) walkExpr(it.expr, locals)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { walkExpr(it, locals) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { walkExpr(it.value, locals) }
            is ExprAst.RangeExpr -> { walkExpr(e.start, locals); walkExpr(e.endValue, locals) }
            is ExprAst.IndexExpr -> { walkExpr(e.target, locals); walkExpr(e.index, locals) }
            is ExprAst.BuiltinCallExpr -> e.args.forEach { walkExpr(it, locals) }
            is ExprAst.NetSubscribeCall -> walkExpr(e.handler, locals)
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { walkExpr(it.value, locals) }
                e.callback?.let { walkExpr(it, locals) }
                e.timeoutMs?.let { walkExpr(it, locals) }
            }
            is ExprAst.IsExpr -> walkExpr(e.target, locals)
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef, is ExprAst.FunRefExpr -> Unit
        }
    }

    /** 若 target 是已知 data 类型的 LocalRef，校验 member 必须在 data 字段集合中。 */
    private fun checkDataMember(
        target: ExprAst,
        member: String,
        position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
        locals: LocalDataTypes,
    ) {
        if (target !is ExprAst.LocalRef) return
        val dataName = locals.get(target.name) ?: return
        val decl = dataTypes[dataName] ?: return
        if (decl.fields.none { it.name == member }) {
            diag.error(DslError.E756_DATA_UNKNOWN_FIELD, position, dataName, member)
        }
    }

    /** 局部 var → data 类型名 的简易作用域追踪（不区分嵌套块）。 */
    private class LocalDataTypes {
        private val map = mutableMapOf<String, String>()
        fun put(name: String, dataName: String) { map[name] = dataName }
        fun get(name: String): String? = map[name]
    }
}
